import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const PostCard = ({ post, currentUser, token, onUpdate }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [liking, setLiking] = useState(false);
  const [localLiked, setLocalLiked] = useState(post.liked_by_me === 1);
  const [localLikesCount, setLocalLikesCount] = useState(parseInt(post.likes_count) || 0);

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/api/posts/${post.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(res.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [post.id, token]);

  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleLike = async () => {
    if (liking) return;
    setLiking(true);

    // Optimistic update
    const wasLiked = localLiked;
    setLocalLiked(!wasLiked);
    setLocalLikesCount(prev => wasLiked ? prev - 1 : prev + 1);

    try {
      await axios.post(
        `${API}/api/posts/${post.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Revert on error
      setLocalLiked(wasLiked);
      setLocalLikesCount(prev => wasLiked ? prev + 1 : prev - 1);
      console.error('Error liking post:', err);
    } finally {
      setLiking(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      const res = await axios.post(
        `${API}/api/posts/${post.id}/comment`,
        { content: commentText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(prev => [...prev, res.data]);
      setCommentText('');
      onUpdate(post.id, { comments_count: parseInt(post.comments_count) + 1 });
    } catch (err) {
      console.error('Error commenting:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
      {/* Post Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center space-x-3">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0"
            style={{ backgroundColor: post.avatar_color || '#25D366' }}
          >
            {getInitials(post.user_name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 truncate">{post.user_name}</p>
            <p className="text-xs text-gray-400">{formatTimeAgo(post.created_at)}</p>
          </div>
          {post.user_id === currentUser.id && (
            <span className="text-xs bg-green-100 text-whatsapp-dark px-2 py-0.5 rounded-full font-medium">
              You
            </span>
          )}
        </div>

        {/* Post Content */}
        <p className="mt-3 text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
          {post.content}
        </p>
      </div>

      {/* Stats bar */}
      {(localLikesCount > 0 || parseInt(post.comments_count) > 0) && (
        <div className="px-4 py-2 border-t border-gray-50 flex items-center justify-between text-xs text-gray-400">
          {localLikesCount > 0 && (
            <div className="flex items-center space-x-1">
              <span className="text-base">❤️</span>
              <span>{localLikesCount} {localLikesCount === 1 ? 'like' : 'likes'}</span>
            </div>
          )}
          {parseInt(post.comments_count) > 0 && (
            <button onClick={handleToggleComments} className="hover:text-whatsapp-green transition-colors">
              {parseInt(post.comments_count)} comment{parseInt(post.comments_count) > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center space-x-1">
        <button
          onClick={handleLike}
          disabled={liking}
          className={`flex-1 flex items-center justify-center space-x-2 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
            localLiked
              ? 'text-red-500 bg-red-50 hover:bg-red-100'
              : 'text-gray-500 hover:bg-gray-50 hover:text-red-400'
          }`}
        >
          <span className="text-lg">{localLiked ? '❤️' : '🤍'}</span>
          <span>Like</span>
        </button>

        <button
          onClick={handleToggleComments}
          className={`flex-1 flex items-center justify-center space-x-2 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
            showComments
              ? 'text-whatsapp-green bg-green-50'
              : 'text-gray-500 hover:bg-gray-50 hover:text-whatsapp-green'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span>Comment</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-gray-100 bg-gray-50">
          {/* Comments List */}
          <div className="px-4 py-3 space-y-3 max-h-64 overflow-y-auto">
            {loadingComments ? (
              <div className="flex justify-center py-4">
                <svg className="animate-spin w-5 h-5 text-whatsapp-green" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : comments.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-2">No comments yet. Be the first!</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex items-start space-x-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: comment.avatar_color || '#25D366' }}
                  >
                    {getInitials(comment.user_name)}
                  </div>
                  <div className="flex-1 bg-white rounded-xl px-3 py-2 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">{comment.user_name}</span>
                      <span className="text-xs text-gray-400">{formatTimeAgo(comment.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5 break-words">{comment.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Comment Input */}
          <div className="px-4 pb-3">
            <form onSubmit={handleComment} className="flex items-center space-x-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
                style={{ backgroundColor: currentUser.avatar_color || '#25D366' }}
              >
                {getInitials(currentUser.name)}
              </div>
              <div className="flex-1 flex items-center bg-white rounded-full border border-gray-200 px-3 py-1.5 shadow-sm">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent"
                />
                <button
                  type="submit"
                  disabled={!commentText.trim() || submittingComment}
                  className="ml-2 text-whatsapp-green disabled:opacity-40 hover:text-whatsapp-dark transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Feed = () => {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(res.data);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim() || submitting) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/posts`,
        { content: newPostContent.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(prev => [res.data, ...prev]);
      setNewPostContent('');
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev =>
      prev.map(p => p.id === postId ? { ...p, ...updates } : p)
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-2xl mx-auto px-4 space-y-4">
        {/* Page Header */}
        <div className="text-center mb-2">
          <h1 className="text-2xl font-black text-whatsapp-darker">📰 Feed S3</h1>
          <p className="text-gray-500 text-sm">Share your thoughts with the class</p>
        </div>

        {/* Create Post Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-start space-x-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0"
              style={{ backgroundColor: user?.avatar_color || '#25D366' }}
            >
              {getInitials(user?.name)}
            </div>
            <form onSubmit={handleCreatePost} className="flex-1">
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder={`What's on your mind, ${user?.name?.split(' ')[0]}?`}
                className="w-full bg-gray-50 rounded-xl px-4 py-3 text-gray-700 placeholder-gray-400 outline-none focus:ring-2 focus:ring-whatsapp-green focus:bg-white transition-all duration-200 resize-none text-sm border border-gray-200"
                rows={3}
              />
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-2 text-gray-400 text-xs">
                  <span>✍️</span>
                  <span>{newPostContent.length} characters</span>
                </div>
                <button
                  type="submit"
                  disabled={!newPostContent.trim() || submitting}
                  className="bg-whatsapp-green hover:bg-whatsapp-dark text-white px-6 py-2 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-md"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Publishing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Post</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <svg className="animate-spin w-10 h-10 text-whatsapp-green mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-500">Loading feed...</p>
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center bg-white rounded-2xl p-10 shadow-sm">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-gray-700 font-bold text-lg mb-2">No posts yet</h3>
            <p className="text-gray-500 text-sm">Be the first to share something with the S3 class!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={user}
                token={token}
                onUpdate={handlePostUpdate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
